import React from 'react';
import { Grid, MenuItem, Select, FormControl, FormHelperText, InputLabel, Box } from '@mui/material';
import { useFormikContext } from 'formik';
import SmallBusinessInit from '../../../../types/initValues/SmallBusiness.init';
import { AgentInputType } from '../../../../types/AgentInputType.types';
import DebounceTextField from '../../../../components/DebounceTextfield';
import InfoButton from '../../../../components/InfoButton';
import { AgentStatus } from '../../../../types/Agent.types';
import Agentv2 from '../../../../types/Agentv2.types';
import PanelCard from './PanelCard';

const ibanInfoText =
  'Die IBAN setzt sich folgendermaßen zusammen: 2-stelliger Ländercode + 2-stellige Prüfziffern + Maximal 30-stellige Kontoidentifikation (bestehend aus den Buchstaben A–Z oder Ziffern). Die IBAN-Länge für Deutschland beträgt: 22 Zeichen. Die IBAN-Länge für Österreich beträgt: 20 Zeichen. Die IBAN-Länge für Italien beträgt: 27 Zeichen. Die IBAN-Länge für Spanien beträgt: 24 Zeichen. ';

const wirtschaftsIdInfoText =
  'Die Wirtschafts-ID setzt sich wie folgt zusammen: DE + 9-stellige Basis-Nummer + Bindestrich + 5-stellige Zusatznummer. Beispiel: DE123456789-00001. Bei deutschen Beratern muss die 9-stellige Basis-Nummer mit der Basis-Nummer der Umsatzsteuer-ID übereinstimmen. Die USt-Nr DE123456789 und die Wirtschafts-ID DE123456789-00001 gehören zum selben Unternehmen.';

function FinancePanel() {
  const { values, handleChange, errors, touched, handleBlur, initialValues, setFieldValue } = useFormikContext<
    AgentInputType | Agentv2
  >();

  const isDisabled = initialValues.status_id === AgentStatus.dismissed || values.status_id === AgentStatus.failed;

  return (
    <Box>
      <PanelCard title="Bankverbindung" subtitle="IBAN, BIC, Bankname">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DebounceTextField
              label="IBAN"
              name="iban"
              aria-label="iban"
              value={values.iban || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.iban}
              error={Boolean(errors.iban) || false}
              fullWidth
              disabled={isDisabled}
              InputProps={{ endAdornment: <InfoButton tooltiptext={ibanInfoText} /> }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              label="BIC"
              name="bic"
              aria-label="bic"
              value={values.bic || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.bic}
              error={Boolean(errors.bic) || false}
              fullWidth
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              label="Bankname"
              name="bank_name"
              aria-label="bank_name"
              value={values.bank_name || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.bank_name}
              error={Boolean(errors.bank_name) || false}
              fullWidth
              disabled={isDisabled}
            />
          </Grid>
        </Grid>
      </PanelCard>

      <PanelCard title="Steuerdaten" subtitle="Steuernummer, USt-ID, Besteuerungsart">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              label="Steuernummer"
              name="tax_nr"
              aria-label="tax_nr"
              value={values.tax_nr || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.tax_nr}
              error={Boolean(errors.tax_nr) || false}
              fullWidth
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              label="Umsatzsteuer ID"
              name="ust_nr"
              aria-label="ust_nr"
              value={values.ust_nr || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.ust_nr}
              error={Boolean(errors.ust_nr) || false}
              fullWidth
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              label="Wirtschafts-ID"
              name="wirtschafts_id"
              aria-label="wirtschafts_id"
              value={values.wirtschafts_id || ''}
              onChange={(e) => {
                const cleanedValue = e.target.value.replace(/\s/g, '');
                setFieldValue('wirtschafts_id', cleanedValue);
              }}
              onBlur={handleBlur}
              helperText={touched.wirtschafts_id && errors.wirtschafts_id ? errors.wirtschafts_id : ''}
              error={Boolean(touched.wirtschafts_id && errors.wirtschafts_id)}
              fullWidth
              disabled={isDisabled}
              placeholder="DE123456789-00001"
              InputProps={{ endAdornment: <InfoButton tooltiptext={wirtschaftsIdInfoText} /> }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={Boolean(errors.is_small_business) || false} disabled={isDisabled}>
              <InputLabel>Besteuerungsart</InputLabel>
              <Select
                label="Besteuerungsart"
                name="is_small_business"
                value={values.is_small_business}
                onChange={handleChange}
                data-testid="is_small_business-select"
              >
                {SmallBusinessInit?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.is_small_business}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebounceTextField
              type="number"
              label="Kreditorennummer"
              name="agent_account_nr"
              aria-label="agent_account_nr"
              value={values.agent_account_nr || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={
                initialValues.status_id === AgentStatus.active ||
                initialValues.status_id === AgentStatus.dismissed ||
                values.status_id === AgentStatus.failed
              }
              helperText={errors.agent_account_nr}
              error={Boolean(errors.agent_account_nr) || false}
              fullWidth
            />
          </Grid>
        </Grid>
      </PanelCard>
    </Box>
  );
}

export default FinancePanel;
